import axios from 'axios';
import { useQuery } from 'react-query';

/**
 * React Query hook for fetching HR Documents
 * @param {Object} params - Query parameters
 * @param {string} params.token - Authentication token
 * @param {number} params.page - Page number (0-based, will be converted to 1-based for API)
 * @param {number} params.limit - Number of items per page
 */
export const useFetchHrDocuments = ({
  token,
  page = 0,
  limit = 10,
  search = '',
}) => {
  return useQuery(
    ['hrDocuments', token, page, limit, search],
    async () => {
      if (!token) {
        throw new Error('Authentication token is missing.');
      }

      const params = new URLSearchParams({ page: page + 1, limit });
      if (search && search.trim()) params.set('search', search.trim());

      const response = await axios.get(`/api/hr-document/all?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const fetchedDocuments = Array.isArray(response.data?.data) ? response.data.data : [];
      
      // Add fullName to each document for display
      const documentsWithFullName = fetchedDocuments.map((item) => {
        const createdBy = item.created_by || {};
        const fullName = createdBy.first_name
          ? `${createdBy.first_name} ${createdBy.middle_name && createdBy.middle_name !== "null"
              ? createdBy.middle_name + " "
              : ""
            }${createdBy.last_name}`
          : createdBy.company_first_name
            ? `${createdBy.company_first_name} ${createdBy.company_last_name ? createdBy.company_last_name : ""
            }`
            : createdBy.company_name;
        return { ...item, fullName };
      });

      return {
        documents: documentsWithFullName,
        total: response.data?.totalCount || 0,
        page: response.data?.page || page + 1,
        limit: response.data?.limit || limit,
        totalPages: response.data?.totalPages || Math.ceil((response.data?.totalCount || 0) / limit),
      };
    },
    {
      staleTime: 5 * 60 * 1000, // Data is fresh for 5 minutes
      cacheTime: 10 * 60 * 1000, // Data remains in cache for 10 minutes
      keepPreviousData: true,
      enabled: !!token, // Only fetch if token exists
      retry: 2, // Retry failed requests up to 2 times
      refetchOnWindowFocus: false, // Don't refetch on window focus
    }
  );
};

